// src/components/RunFormFields.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, MenuItem, Button, Stack,
  Select, FormControl, LinearProgress, Chip, Grid
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { listDictionaries, getUserSettings } from "../utils/api";
import { PLATFORMS, labelFor } from "../utils/labelUtils";


import { Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// const PLATFORMS = ["bigquery", "databricks", "postgres", "redshift", "snowflake", "sqlite"];

export default function RunFormFields({ preselectDictId = "", initialValues = {}, onCancel, onSuccess }) {
  const theme = useTheme();

  const [dicts, setDicts] = useState([]);
  const [dictId, setDictId] = useState(preselectDictId || "");
  const [form, setForm] = useState({
    run_name: "",
    platform: null,
    project: "",
    dataset: "",
    problem_statement: "",
    business_output_mode: "standard",
  });

  // ✅ Preload existing values (for rerun)
 useEffect(() => {
   if (initialValues && Object.keys(initialValues).length > 0) {
     setForm((prev) => ({
       ...prev,
       run_name: initialValues.run_name || prev.run_name,
       platform: initialValues.platform || prev.platform,
       project: initialValues.project || prev.project,
       dataset: initialValues.dataset || prev.dataset,
       problem_statement: initialValues.problem_statement || prev.problem_statement,
       business_output_mode:
         initialValues.business_output_mode === "none" || initialValues.business_output_mode === "standard"
           ? initialValues.business_output_mode
           : prev.business_output_mode,
     }));
     if (initialValues.dict_id) setDictId(initialValues.dict_id);
   }
 }, [initialValues]);
    
  const [phase, setPhase] = useState("idle");
  const [runId, ] = useState(null);
  const [error, setError] = useState(null);
  const busy = phase === "queued" || phase === "running";

  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // ---- Load dictionaries ----
  useEffect(() => {
    (async () => {
      try {
        const rows = await listDictionaries();
        setDicts(Array.isArray(rows) ? rows : []);
        if (initialValues?.dict_id && rows?.some(d => d.id === initialValues.dict_id)) {
           setDictId(initialValues.dict_id);
         } else if (!dictId && rows?.length) {
           setDictId(rows[0].id);
         }
      } catch (e) {
        console.error("listDictionaries error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Load default settings ----
    useEffect(() => {
      (async () => {
        try {
          const settings = await getUserSettings();
          setForm((f) => ({
            ...f,
            platform: f.platform || settings.defaultPlatform || PLATFORMS[0],
            project: f.project || settings.defaultProject || "",
            dataset: f.dataset || settings.defaultDataset || "",
          }));
        } catch (e) {
          console.error("getUserSettings error:", e);
        } finally {
          setSettingsLoaded(true);
        }
      })();
    }, []);

  useEffect(() => {
    if (preselectDictId) setDictId(preselectDictId);
  }, [preselectDictId]);

  // ---- Validation ----
  const canSubmit = useMemo(
    () =>
      !!form.run_name &&
      !!dictId &&
      !!form.platform &&
      !!form.dataset &&
      !!form.problem_statement,
    [form, dictId]
  );

  // ---- Submit handler ----
  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!canSubmit) return;
    try {
      setPhase("queued");
      const dictObj = dicts.find((d) => d.id === dictId);
        const values = {
          dictId,
          dict_name: dictObj?.name || dictObj?.filename || "Dictionary",
          run_name: form.run_name,
          platform: form.platform,
          project: form.project,
          dataset: form.dataset,
          problem_statement: form.problem_statement,
          business_output_mode: form.business_output_mode,
        };
        onSuccess?.(values);  // hand off to dialog
        
    } catch (err) {
      console.error(err);
      setPhase("failed");
      setError(err?.response?.data?.message || err?.message || "Run failed.");
    }
  };

  return (
    <Box component="form" onSubmit={submit}>
      <Grid container columnSpacing={3} rowSpacing={3}>
        {/* Run Name */}
        <Grid item xs={12}>
          <Typography variant="paragraph" sx={{ typography: theme.typography.paragraph }} gutterBottom>
            Run Name <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter run name"
            required
            value={form.run_name}
            onChange={(e) => setForm({ ...form, run_name: e.target.value })}
            sx={{ typography: theme.typography.paragraph }}
          />
        </Grid>

        {/* SQL Platform + Data Dictionary */}
        <Grid item xs={12} md={6}>
          <Typography variant="paragraph" sx={{ typography: theme.typography.paragraph }} gutterBottom>
            SQL Platform <span style={{ color: "red" }}>*</span>
          </Typography>
          <FormControl fullWidth required>
            <Select
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              sx={{ typography: theme.typography.paragraph }}
            >
              {PLATFORMS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="paragraph" sx={{ typography: theme.typography.paragraph }} gutterBottom>
            Data Dictionary <span style={{ color: "red" }}>*</span>
          </Typography>
          <FormControl fullWidth required>
            <Select value={dictId} onChange={(e) => setDictId(e.target.value)} sx={{ typography: theme.typography.paragraph }}>
              {dicts.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name || d.filename || d.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Data Project + Dataset Name */}
        <Grid item xs={12} md={6}>
          <Typography variant="paragraph" sx={{ typography: theme.typography.paragraph }} gutterBottom>
            {settingsLoaded ? labelFor("project", form.platform) : ""} <span style={{ color: "red" }}>*</span>
          </Typography>
          <Tooltip
            title={
              <Typography sx={{ fontSize: "0.95rem", lineHeight: 1.4 }}>
                Name of the {labelFor("project", form.platform).toLowerCase()} dedicated for the ML task
              </Typography>
            }
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            fullWidth
            placeholder={settingsLoaded ? `Enter ${labelFor("project", form.platform).toLowerCase()} name` : ""}
            value={form.project}
            onChange={(e) => setForm({ ...form, project: e.target.value })}
            sx={{ typography: theme.typography.paragraph }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="paragraph" sx={{ typography: theme.typography.paragraph }} gutterBottom>
            {settingsLoaded ? labelFor("dataset", form.platform) : ""} <span style={{ color: "red" }}>*</span>
          </Typography>
          <Tooltip
            title={
              <Typography sx={{ fontSize: "0.95rem", lineHeight: 1.4 }}>
                Name of the {labelFor("dataset", form.platform).toLowerCase()} dedicated for the ML task (e.g. scratch_data_science)
              </Typography>
            }
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            fullWidth
            placeholder={settingsLoaded ? `Enter ${labelFor("dataset", form.platform).toLowerCase()} name` : ""}
            required
            value={form.dataset}
            onChange={(e) => setForm({ ...form, dataset: e.target.value })}
            sx={{ typography: theme.typography.paragraph }}
          />
        </Grid>

        {/* Business output (insights / decision UI) */}
        <Grid item xs={12} md={6}>
          <Typography variant="paragraph" sx={{ typography: theme.typography.paragraph }} gutterBottom>
            Business output
          </Typography>
          <FormControl fullWidth>
            <Select
              value={form.business_output_mode}
              onChange={(e) => setForm({ ...form, business_output_mode: e.target.value })}
              sx={{ typography: theme.typography.paragraph }}
            >
              <MenuItem value="standard">Standard — business decisions + technical notebook</MenuItem>
              <MenuItem value="none">Technical only — hide business insights for this run</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Business Question */}
        <Grid item xs={12}>
          <Typography variant="paragraph" sx={{ typography: theme.typography.paragraph }} gutterBottom>
            Your Business Question <span style={{ color: "red" }}>*</span>
          </Typography>
            <Tooltip title={
                <Typography sx={{ fontSize: "0.95rem", lineHeight: 1.4 }}>
                  Detailed description of your prediction problem, prediction outcome, and prediction level.
                  <br />
                  Example: "Predict next 7 days sales. Prediction level: category. Outcome: total category sales in 7 days."
                </Typography>
              }>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            fullWidth
            placeholder="Describe the business question…"
            multiline
            minRows={3}
            required
            value={form.problem_statement}
            onChange={(e) => setForm({ ...form, problem_statement: e.target.value })}
            sx={{ typography: theme.typography.paragraph }}
          />
        </Grid>
      </Grid>

      {busy && <LinearProgress sx={{ mt: 2 }} />}


        
        
      {/* Buttons */}
      <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button onClick={onCancel} disabled={busy} color="secondary">
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={!canSubmit || busy} color="primary">
          {phase === "queued" ? "Queued…" : phase === "running" ? "Running…" : "Submit"}
        </Button>
        {phase !== "idle" && (
          <Chip
            size="small"
            label={runId ? `${phase.toUpperCase()} • ${runId}` : phase.toUpperCase()}
            color={
              phase === "succeeded" ? "success" : phase === "failed" ? "error" : "default"
            }
            variant="outlined"
          />
        )}
      </Stack>

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}